import {cs}                     from "@Common/react/chain-services";
import {consumeContext}         from "@Common/react/context";
import {scopedState}            from "@Common/react/scoped-state";
import {UseState}               from "@Common/react/use-state";
import {cnv, snv}               from "@Common/utils/arr-path";
import {arrEquals, sum, unique} from "@Common/utils/collections";
import {chain}                  from "@Common/utils/fs";
import {omit}                   from "@Common/utils/objects";

import {WarningMailingListModal} from "../../../mailing-ui/ml-list-table/warning-mailing-list-modal/warning-mailing-list-modal";
import {groupItemEditors}        from "../../minors/group-item-editors";
import {ErrorMessagesLogic}      from "../error-messages-logic";

import {DedupeLogic} from "./dedupe-logic";

export const AddressListsLogic = ({entityId, item, next: rootNext}) => cs(
    consumeContext("userPrefs"),
    ["state", ({userPrefs}, next) => UseState({
        getInitValue: () => (
            !item ? chain(
                userPrefs.defaultMailingList?.map((ml) => ml.id),
                (_) => _ ?? [],
                (_) => ({
                    saved: null,
                    current: _,
                }),
            ) : chain(
                item.selections?.mailingListIds?.map((ml) => ml.id),
                (_) => _ ?? [],
                (_) => ({
                    saved: _,
                    current: _,
                }),
            )
        ),
        next,
    })],
    consumeContext("mailingLists"),

    ["errorMessages1", ({}, next) => ErrorMessagesLogic({next})],

    ["calSelected", ({mailingLists, state}, next) => next(chain(
        state.value.current?.filter(
            (id) => mailingLists.value?.find((ml) => ml.id == id)
        )??[],
        unique,
    ))],
    ["quantity", ({calSelected, mailingLists}, next) => next(
        sum(calSelected, (id) => +(mailingLists.value?.find((ml) => ml.id == id)?.count ?? 0))
    )],

    ["dedupe", ({calSelected}, next) => DedupeLogic({
        mlIds: calSelected,
        item,
        next,
    })],
    ["warningMailingListModal", (_, next) => WarningMailingListModal({entityId, next})],
    ["step1", ({state, calSelected}, next) => rootNext({
        state: {...scopedState(state, ["current"]), value: calSelected},
        cleanItem: (item) => cnv(item, ["selections"], (_) => omit(_, ["mailingListIds"])),
        next,
    })],


    ["step2", ({step1, quantity, calSelected, dedupe}, next) => step1.next({
        quantity: quantity - dedupe.quantity,
        mailingListIds: calSelected,
        next,
    })],

    ["errorMessages", ({step2, quantity, errorMessages1}, next) => next(errorMessages1((() => {
        return [
            {
                hasError: !quantity,
                errorMessage: "Please select a mailing list to continue",
                code: "no_mailing_list_selected",
            },

            {
                hasError: step2.totalQuantity && !step2.paperPrice,
                errorMessage: `Sorry, we have no pricing policy for selected quantity (${step2.totalQuantity})`
            },
        ];
    })()))],


    ["warningLists", ({mailingLists, calSelected}, next) => next(
        mailingLists.value?.filter(
            (ml) => (calSelected.findIndex((id) => id == ml.id) > -1 && ml.warningCount > 0)
        )
    )],

    ({state, step2, errorMessages, mailingLists, calSelected, warningLists, warningMailingListModal, dedupe}) => step2.next({
        itemEditor: groupItemEditors([
            {
                $name: "address lists",
                changed: !arrEquals(state.value.saved, calSelected, (v1, v2) => v1 == v2),
                prepareSave: (item) => ({
                    newValue: snv(item, ["selections", "mailingListIds"], calSelected.map((id) => ({id: "" + id}))),
                    onSaved: () => {
                        // console.log("Saving state", ran, state.value.current, a1);
                        return state.change((s) => ({
                            ...s,
                            saved: calSelected,
                        }));
                    },
                }),

                showWarnings: async () => {
                    if ( warningLists?.length > 0 ) {
                        return await warningMailingListModal.show({
                            mailingLists: warningLists,
                        });
                    } else {
                        return true;
                    }
                },

                valid: !errorMessages.active && !mailingLists.loading,
                errorsHandling: {
                    highlightErrors: errorMessages.highlightErrors,
                },
            },
            dedupe.itemEditor,
        ].filter(v=>v)),

        ui: {
            errorMessages,
            state: {
                ...scopedState(state, ["current"]),
                value: calSelected,
            },
            // totalQuantity: step2.totalQuantity,
            // minQuantity: step2.paperPrices && getMinQty(step2.paperPrices),
            dedupe: dedupe.ui,
        },
    }),
);
